/**
 * A bounded set of whole-day integers, shown as removable chips plus an "add one" input —
 * reused for both the trial's reminder lead days (sorted DESC, "days BEFORE the trial
 * ends") and the dunning follow-up days (sorted ASC, "days AFTER the first failed
 * charge"), per contract §9.
 *
 * Display order is the ONLY thing that differs by direction: the underlying array the
 * caller holds keeps whatever order edits produced, because the server normalizes and
 * re-sorts it on save (contract §2) — re-sorting here too would just be a second,
 * possibly-disagreeing copy of that rule.
 *
 * A day that is now out of `[dayMin, dayMax]` because a SIBLING field changed later
 * (the trial got shorter after a lead day was added, say) is not caught here — catching
 * it would mean re-deriving the same lt_trial_length / lt_grace_days rule the server
 * already owns (contract §6 VIOLATION_RULES). The server's 422 is authoritative; the
 * caller renders it via `LIFECYCLE.fieldError(listPath)`, same as any other violation.
 */
import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useT } from "../../lib/LanguageContext";

/**
 * @param {number[]} values
 * @param {(next: number[]) => void} onChange
 * @param {number} max max items (bounds.max_reminders | bounds.max_follow_ups)
 * @param {number} dayMin bound for one item
 * @param {number} dayMax bound for one item (may be < dayMin, meaning no value is legal)
 * @param {"desc"|"asc"} order display sort
 * @param {{editor,chip,chipRemove,input,add}} testIds LIFECYCLE ids, chip/chipRemove are fns of display index
 * @param {string} unitKey i18n key for one chip's text, interpolated with {n}
 * @param {boolean} [disabled]
 * @param {string} id DOM id prefix, unique per instance (also the label's `htmlFor` target)
 * @param {string} [labelKey] i18n key for the group's visible label
 */
export default function LeadDaysEditor({
  id,
  labelKey,
  values,
  onChange,
  max,
  dayMin,
  dayMax,
  order = "desc",
  testIds,
  unitKey,
  disabled = false,
}) {
  const t = useT("superadmin");
  const [draftValue, setDraftValue] = useState("");
  const [inputError, setInputError] = useState("");

  const sorted = [...values].sort((a, b) => (order === "desc" ? b - a : a - b));
  const rangeInvalid = dayMax < dayMin;
  const atMax = values.length >= max;
  const addBlocked = disabled || atMax || rangeInvalid;

  const remove = (value) => {
    if (disabled) return;
    onChange(values.filter((v) => v !== value));
  };

  const add = () => {
    if (addBlocked || draftValue === "") return;
    const n = Number(draftValue);
    if (!Number.isInteger(n)) { setInputError(t("lifecycle.dayInput.invalid")); return; }
    if (n < dayMin || n > dayMax) {
      setInputError(t("lifecycle.dayInput.range", { min: dayMin, max: dayMax }));
      return;
    }
    if (values.includes(n)) { setInputError(t("lifecycle.dayInput.duplicate")); return; }
    onChange([...values, n]);
    setDraftValue("");
    setInputError("");
  };

  const inputId = `${id}-input`;
  const errorId = `${id}-error`;

  return (
    <div data-testid={testIds.editor}>
      {labelKey && (
        <p id={id} className="text-sm font-medium text-slate-700 mb-1.5">{t(labelKey)}</p>
      )}
      <ul className="flex flex-wrap gap-2 mb-2" aria-labelledby={labelKey ? id : undefined}>
        {sorted.length === 0 && (
          <li className="text-xs text-slate-400 list-none">{t("lifecycle.dayInput.empty")}</li>
        )}
        {sorted.map((v, i) => (
          <li key={v} className="list-none">
            <span
              data-testid={testIds.chip(i)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-slate-700"
            >
              {unitKey ? t(unitKey, { n: v }) : v}
              <button
                type="button"
                onClick={() => remove(v)}
                disabled={disabled}
                aria-label={t("lifecycle.dayInput.remove", { n: v })}
                data-testid={testIds.chipRemove(i)}
                className="text-slate-400 hover:text-rose-600 disabled:opacity-50"
              >
                ×
              </button>
            </span>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2">
        <label htmlFor={inputId} className="sr-only">{t("lifecycle.dayInput.addLabel")}</label>
        <Input
          id={inputId}
          type="number"
          min={dayMin}
          max={dayMax}
          value={draftValue}
          onChange={(e) => { setDraftValue(e.target.value); setInputError(""); }}
          disabled={addBlocked}
          data-testid={testIds.input}
          aria-describedby={inputError ? errorId : undefined}
          aria-invalid={!!inputError}
          className="w-24"
        />
        <Button
          type="button"
          variant="outline"
          onClick={add}
          disabled={addBlocked || draftValue === ""}
          data-testid={testIds.add}
        >
          {t("lifecycle.dayInput.add")}
        </Button>
      </div>
      {inputError && (
        <p id={errorId} role="alert" className="text-xs text-rose-600 mt-1">{inputError}</p>
      )}
    </div>
  );
}
